/**
 * ImageExtractor Service
 * Extract và download images/videos từ URLs
 */

export interface MediaItem {
  url: string
  type: 'image' | 'video'
  localPath?: string
}

import { API_CONFIG } from '../../config/api.config'

class ImageExtractor {
  private previewUrlCache = new Map<string, string>()
  /**
   * Extract image/video links từ text
   */
  extractMediaLinks(text: string): string[] {
    // Improved regex để tìm image/video URLs
    // Support URLs with query params (e.g., ?w=800, &format=webp)
    const imagePattern = /https?:\/\/[^\s\)]+\.(jpg|jpeg|png|gif|webp|svg)(\?[^\s\)]*)?/gi
    const videoPattern = /https?:\/\/[^\s\)]+\.(mp4|webm|ogg|mov)(\?[^\s\)]*)?/gi

    // Also match YouTube URLs (but we won't download them)
    const youtubePattern = /https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[^\s\)]+/gi

    // Match local file paths (Windows: D:\..., Unix: /...)
    const localPathPattern = /[A-Z]:\\(?:[^\\/:*?"<>|\r\n]+\\)*[^\\/:*?"<>|\r\n]+\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|ogg|mov)/gi
    const unixPathPattern = /\/[^\s\)]+\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|ogg|mov)/gi

    // Match any URL that might be media (even without extension)
    const genericMediaPattern = /https?:\/\/[^\s\)]*(unsplash|imgur|youtube|youtu\.be|vimeo|cloudinary|cdn)[^\s\)]*/gi

    const imageLinks = text.match(imagePattern) || []
    const videoLinks = text.match(videoPattern) || []
    const youtubeLinks = text.match(youtubePattern) || []
    const genericLinks = text.match(genericMediaPattern) || []
    const localPaths = text.match(localPathPattern) || []
    const unixPaths = text.match(unixPathPattern) || []

    // Combine all, remove duplicates, and filter out non-media URLs
    const allLinks = [...imageLinks, ...videoLinks, ...youtubeLinks, ...genericLinks, ...localPaths, ...unixPaths]
    const uniqueLinks = [...new Set(allLinks)]

    // Filter to ensure they look like media URLs
    return uniqueLinks.filter(url => {
      const lowerUrl = url.toLowerCase()
      const hasExtension = lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|ogg|mov)(\?|$)/i)
      const isYouTube = lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')
      const isLocalPath = /^[A-Z]:\\|^\//.test(url) // Windows path (D:\) or Unix path (/)

      // Keep direct media links (with extension) and local paths, but skip YouTube (handled separately)
      return (hasExtension || isLocalPath) && !isYouTube
    })
  }

  /**
   * Download image/video từ URL hoặc copy từ local path và lưu local
   */
  async downloadAndSaveMedia(
    url: string,
    teacherId: string,
    lessonId: string
  ): Promise<string> {
    try {
      console.log(`📥 Processing media from: ${url}`)

      const lowerUrl = url.toLowerCase()
      if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
        console.log('ℹ️ Skipping download for YouTube link, returning original URL')
        return url
      }

      // ✅ QUAN TRỌNG: Xử lý relative paths từ database (bank/Images/...)
      const isRelativeBankPath = url.includes('bank/Images/') || url.includes('Bank/Images/') ||
        url.includes('/bank/Images/') || url.includes('/Bank/Images/')

      if (isRelativeBankPath) {
        console.log(`📁 Detected relative bank path: ${url}`)

        // Normalize path: đảm bảo có leading slash
        const normalizedPath = url.startsWith('/') ? url : `/${url}`
        // Normalize case: Bank -> bank
        let cleanPath = normalizedPath.replace(/\/[Bb]ank\//, '/bank/')

        // Use API_CONFIG for bank URL
        if (cleanPath.startsWith('/bank/')) {
          cleanPath = cleanPath.replace('/bank', API_CONFIG.bank.baseUrl)
        }

        try {
          // Fetch file từ public folder
          console.log(`📥 Fetching file from public folder: ${cleanPath}`)
          const response = await fetch(cleanPath, {
            mode: 'cors',
            credentials: 'omit'
          })

          if (!response.ok) {
            console.warn(`⚠️ Could not fetch ${cleanPath} from public folder (${response.status}), trying alternative paths`)
            // Thử các path alternatives
            const pathParts = cleanPath.split('/')
            const filename = pathParts[pathParts.length - 1]
            const alternativePaths = [
              `${API_CONFIG.bank.baseUrl}/Images/${filename}`,
              `${API_CONFIG.bank.baseUrl}/Images/${filename}`, // Case insensitive check not needed if we use baseUrl, but keeping structure
              cleanPath
            ]

            let fetched = false
            for (const altPath of alternativePaths) {
              try {
                const altResponse = await fetch(altPath, { mode: 'cors', credentials: 'omit' })
                if (altResponse.ok) {
                  const blob = await altResponse.blob()
                  return await this.saveFetchedBlobToTeacherDirectory(blob, filename, teacherId, lessonId, url)
                }
              } catch (e) {
                continue
              }
            }

            if (!fetched) {
              throw new Error(`Could not fetch file from any alternative path`)
            }
          } else {
            const blob = await response.blob()
            const filename = cleanPath.split('/').pop() || `image-${Date.now()}.png`
            return await this.saveFetchedBlobToTeacherDirectory(blob, filename, teacherId, lessonId, url)
          }
        } catch (error) {
          console.error(`❌ Error processing relative bank path ${url}:`, error)
          // Fallback: return original path, sẽ được xử lý sau
          return url
        }
      }

      // Check if it's a local file path (Windows/Unix absolute)
      const isLocalPath = /^[A-Z]:\\|^\//.test(url) && !url.startsWith('http') && !isRelativeBankPath

      if (isLocalPath) {
        // Try to read local file and copy to public folder
        try {
          console.log(`📁 Detected local file path: ${url}`)

          // In browser, we can't directly access local file system
          // But we can try to fetch it if it's in the public folder or accessible
          // For now, we'll try to construct a relative path
          const pathParts = url.split(/[/\\]/)
          const filename = pathParts[pathParts.length - 1]

          // Try to find if file exists in public/teacher_content
          const possiblePaths = [
            `/teacher_content/${teacherId}/images/${filename}`,
            `/teacher_content/${teacherId}/videos/${filename}`,
            `/teacher_content/images/${filename}`,
            `/teacher_content/videos/${filename}`,
            url.replace(/^[A-Z]:\\/, '/').replace(/\\/g, '/') // Convert Windows path to web path
          ]

          // For now, return the first possible path (will be checked when rendering)
          // In production, this should be handled by backend API
          const isVideo = /\.(mp4|webm|ogg|mov)/i.test(filename)
          const folder = isVideo ? 'videos' : 'images'
          const localPath = `/teacher_content/${teacherId}/${folder}/${filename}`

          console.log(`📁 Using local path: ${localPath}`)
          return localPath
        } catch (error) {
          console.warn(`⚠️ Could not process local path ${url}, returning as-is:`, error)
          return url
        }
      }

      // Fetch media from HTTP/HTTPS URL
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }

      const blob = await response.blob()

      // Determine file type
      const isVideo = /\.(mp4|webm|ogg|mov)/i.test(url)
      const contentType = response.headers.get('content-type') || blob.type

      // Generate filename: 6 random chars
      const ext = this.getExtensionFromUrl(url) || this.getExtensionFromMimeType(contentType)
      const randomStr = Math.random().toString(36).substring(2, 8)
      const filename = `${randomStr}.${ext}`

      return await this.saveFetchedBlobToTeacherDirectory(blob, filename, teacherId, lessonId, url)

    } catch (error) {
      console.error(`❌ Error downloading media from ${url}:`, error)
      // Return original URL as fallback
      return url
    }
  }

  /**
   * Helper: Save fetched blob to teacher directory với cấu trúc L{lop}/C{chuong}/B{bai}
   */
  public async saveFetchedBlobToTeacherDirectory(
    blob: Blob,
    filename: string,
    teacherId: string,
    lessonId: string,
    originalUrl: string
  ): Promise<string> {
    // Determine file type
    const isVideo = /\.(mp4|webm|ogg|mov)/i.test(filename) || blob.type.startsWith('video/')
    const contentType = blob.type

    // Parse lessonId để tạo cấu trúc thư mục L{lop}/C{chuong}/B{bai}
    const lessonInfo = this.parseLessonKey(lessonId)

    // ✅ Extract filename from path (remove bank/Images/ prefix)
    // bank/Images/De_test_Ebook5_q5_img1_fd32cb.png → De_test_Ebook5_q5_img1_fd32cb.png
    const cleanFilename = filename.includes('/') ? filename.split('/').pop()! : filename
    const sanitizedFilename = this.sanitizeFileName(cleanFilename)

    let localPath: string

    if (lessonInfo) {
      // ✅ FIX: Save quiz images to Lxx/Cxx/Bxx/images/ (same level as quiz JSON files)
      // Structure: teacher_content/{teacherId}/L11/C1/B01/images/filename.png
      const baiPadded = lessonInfo.bai.padStart(2, '0')

      if (isVideo) {
        localPath = `/teacher_content/${teacherId}/L${lessonInfo.lop}/C${lessonInfo.chuong}/B${baiPadded}/${sanitizedFilename}`
      } else {
        // Images go to images/ subfolder
        localPath = `/teacher_content/${teacherId}/L${lessonInfo.lop}/C${lessonInfo.chuong}/B${baiPadded}/images/${sanitizedFilename}`
        console.log(`📁 [QUIZ IMAGE] Saving to lesson images folder: ${localPath}`)
      }
    } else {
      // Fallback: shared images folder
      const folder = isVideo ? 'videos' : 'images'
      localPath = `/teacher_content/${teacherId}/${folder}/${sanitizedFilename}`
      console.warn(`⚠️ Could not parse lessonId "${lessonId}", using fallback: ${localPath}`)
    }

    // Save to public directory (via File System API or IndexedDB)
    const serverPath = await this.saveFileToPublic(blob, localPath, teacherId, lessonId, 'quiz')
    this.registerPreviewUrl(serverPath, blob)

    console.log(`✅ Media saved to: ${serverPath} (from ${originalUrl})`)
    return serverPath
  }

  /**
   * Download multiple media files
   */
  async downloadAndSaveMultipleMedia(
    urls: string[],
    teacherId: string,
    lessonId: string
  ): Promise<string[]> {
    const results = await Promise.allSettled(
      urls.map(url => this.downloadAndSaveMedia(url, teacherId, lessonId))
    )

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value
      } else {
        console.warn(`⚠️ Failed to download ${urls[index]}:`, result.reason)
        return urls[index] // Return original URL as fallback
      }
    })
  }

  /**
   * Save embedded media (base64) extracted from Word files
   */
  async saveEmbeddedMediaFromBase64(
    base64Data: string,
    contentType: string,
    teacherId: string,
    lessonId: string,
    mediaType: 'image' | 'video' = 'image',
    options?: { lessonKey?: string; contentTypeParam?: string }
  ): Promise<string> {
    try {
      if (!base64Data) {
        throw new Error('Empty base64 data')
      }

      const blob = this.base64ToBlob(base64Data, contentType || (mediaType === 'video' ? 'video/mp4' : 'image/png'))
      return await this.saveMediaBlob(blob, teacherId, lessonId, {
        mediaType,
        contentType: contentType || blob.type,
        suggestedName: undefined,
        lessonKey: options?.lessonKey || lessonId,
        contentTypeParam: options?.contentTypeParam || 'theory'
      })
    } catch (error) {
      console.error('❌ Error saving embedded media from base64:', error)
      return `data:${contentType};base64,${base64Data}`
    }
  }

  /**
   * Parse lessonKey để lấy lop, chuong, bai
   * Hỗ trợ các format:
   * - "lop-11-chuong-1-bai-1" -> { lop: "11", chuong: "1", bai: "1" }
   * - "L11_C1_B1" -> { lop: "11", chuong: "1", bai: "1" }
   * - "11-1-1" -> { lop: "11", chuong: "1", bai: "1" }
   */
  public parseLessonKey(lessonKey: string): { lop: string; chuong: string; bai: string } | null {
    if (!lessonKey) return null

    // Format: "lop-11-chuong-1-bai-1"
    const format1Match = lessonKey.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/i)
    if (format1Match) {
      return {
        lop: format1Match[1],
        chuong: format1Match[2],
        bai: format1Match[3]
      }
    }

    // Format: "L11_C1_B1" hoặc "L11-C1-B1"
    const format2Match = lessonKey.match(/L(\d+)[_-]C(\d+)[_-]B(\d+)/i)
    if (format2Match) {
      return {
        lop: format2Match[1],
        chuong: format2Match[2],
        bai: format2Match[3]
      }
    }

    // Format: "11-1-1" (chỉ số)
    const format3Match = lessonKey.match(/^(\d+)-(\d+)-(\d+)$/)
    if (format3Match) {
      return {
        lop: format3Match[1],
        chuong: format3Match[2],
        bai: format3Match[3]
      }
    }

    return null
  }

  async saveMediaBlob(
    blob: Blob,
    teacherId: string,
    lessonId: string,
    options?: { mediaType?: 'image' | 'video'; contentType?: string; suggestedName?: string; lessonKey?: string; contentTypeParam?: string }
  ): Promise<string> {
    console.log('📸 [SAVE MEDIA] saveMediaBlob called:', {
      teacherId,
      lessonId,
      blobSize: blob.size,
      blobType: blob.type,
      options
    })

    const mediaType = options?.mediaType || (blob.type.startsWith('video') ? 'video' : 'image')
    const extFromName = options?.suggestedName?.split('.').pop()
    const ext =
      (extFromName && extFromName.toLowerCase()) ||
      this.getExtensionFromMimeType(options?.contentType || blob.type || (mediaType === 'video' ? 'video/mp4' : 'image/png'))

    // Generate short random filename if not suggested
    const randomStr = Math.random().toString(36).substring(2, 8)
    const sanitizedName = options?.suggestedName
      ? this.sanitizeFileName(options.suggestedName)
      : `${randomStr}.${ext}`

    // Parse lessonKey để tạo cấu trúc thư mục L{lop}/C{chuong}/B{bai}
    const lessonKey = options?.lessonKey || lessonId
    const lessonInfo = this.parseLessonKey(lessonKey)

    let localPath: string
    if (lessonInfo) {
      // Cấu trúc mới: /teacher_content/{teacherId}/L{lop}/C{chuong}/B{bai}/{filename}
      // Pad số bài với 0: 1 -> 01, 10 -> 10
      const baiPadded = lessonInfo.bai.padStart(2, '0')
      localPath = `/teacher_content/${teacherId}/L${lessonInfo.lop}/C${lessonInfo.chuong}/B${baiPadded}/${sanitizedName}`
      console.log(`📁 Using structured path: ${localPath}`)
    } else {
      // Fallback: cấu trúc cũ nếu không parse được lessonKey
      const folder = mediaType === 'video' ? 'videos' : 'images'
      localPath = `/teacher_content/${teacherId}/${folder}/${sanitizedName}`
      console.warn(`⚠️ Could not parse lessonKey "${lessonKey}", using fallback path: ${localPath}`)
    }

    // Upload to server and get server path
    const serverPath = await this.saveFileToPublic(
      blob,
      localPath,
      teacherId,
      lessonKey,
      options?.contentTypeParam || (mediaType === 'video' ? 'simulation' : 'theory')
    )

    this.registerPreviewUrl(serverPath, blob)
    console.log(`📦 Saved media blob: ${serverPath}`)
    return serverPath
  }

  /**
   * Extract lop/chuong/bai from current URL
   */
  private extractLessonFromUrl(): { lop: string; chuong: string; bai: string } | null {
    if (typeof window === 'undefined') return null

    try {
      // URL format: /lop/11/chuong/1/bai/1
      const pathMatch = window.location.pathname.match(/\/lop\/(\d+)\/chuong\/(\d+)\/bai\/(\d+)/i)
      if (pathMatch) {
        return {
          lop: pathMatch[1],
          chuong: pathMatch[2],
          bai: pathMatch[3]
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not extract lesson from URL:', error)
    }

    return null
  }

  async resolveMediaSrc(path: string): Promise<string> {
    if (!path) return path

    // Already resolved URLs (data URLs, blob URLs, full HTTP URLs)
    if (path.startsWith('data:') || path.startsWith('blob:') || path.startsWith('http')) {
      // ✅ FIX: Chỉ dùng API proxy cho video files (phần 3 - simulation) để tránh CORS
      // YouTube URLs (phần 2) và images (phần 1) không bị ảnh hưởng
      const isVideoFile = /\.(mp4|webm|ogg|mov)/i.test(path)
      const isYouTube = path.includes('youtube.com') || path.includes('youtu.be')

      // Chỉ dùng API proxy cho video files từ teacher-content, không phải YouTube
      if (isVideoFile && !isYouTube && path.includes('/teacher_content/')) {
        // Extract relative path from Python backend
        const contentMatch = path.match(/\/teacher_content\/(.+)$/)
        if (contentMatch) {
          const relativePath = contentMatch[1]
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
          const proxyUrl = `${API_BASE_URL}/teacher/media?path=${encodeURIComponent(relativePath)}`
          console.log(`🔧 [Python Backend] Using proxy for video: ${path} → ${proxyUrl}`)
          return proxyUrl
        }
      }
      // YouTube URLs và images giữ nguyên (không bị ảnh hưởng)
      return path
    }

    // Check cache for IndexedDB blob URLs first
    const cached = this.previewUrlCache.get(path)
    if (cached) {
      return cached
    }

    // Resolve teacher_content paths to Python backend URL
    if (path.startsWith('/teacher_content/') || path.startsWith('teacher_content/')) {
      const cleanPath = path.startsWith('/') ? path : `/${path}`
      // ✅ FIX: Remove /api suffix for static file access
      let baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      baseUrl = baseUrl.replace(/\/api\/?$/, '')

      // Python backend lưu file theo cấu trúc: teacher_content/{teacherId}/L{lop}/C{chuong}/B{bai}/{filename}
      // ✅ QUAN TRỌNG: Nếu path thiếu L{lop}/C{chuong}/B{bai}, tự động thêm vào

      let pathsToTry: string[] = []

      // Extract teacherId từ path
      const teacherIdMatch = cleanPath.match(/\/teacher-content\/([^\/]+)/)
      const teacherId = teacherIdMatch ? teacherIdMatch[1] : null

      // Kiểm tra xem path đã có cấu trúc L{lop}/C{chuong}/B{bai} chưa
      const hasStructuredPath = /\/L\d+\/C\d+\/B\d+\//.test(cleanPath)

      if (!hasStructuredPath && teacherId) {
        // ✅ FIX: Path thiếu L{lop}/C{chuong}/B{bai}, cần thêm vào
        console.log(`🔧 [Path Fix] Path thiếu L/C/B structure: ${cleanPath}`)

        // Extract filename từ path
        const pathParts = cleanPath.split('/').filter(p => p)
        const filename = pathParts.pop() || ''

        // Thử extract từ URL hiện tại
        const lessonInfo = this.extractLessonFromUrl()
        if (lessonInfo) {
          const baiPadded = lessonInfo.bai.padStart(2, '0')
          const fixedPath = `/teacher_content/${teacherId}/L${lessonInfo.lop}/C${lessonInfo.chuong}/B${baiPadded}/${filename}`
          pathsToTry.push(fixedPath)
          console.log(`✅ [Path Fix] Thêm L/C/B từ URL: ${fixedPath}`)
        }

        // Thử extract từ filename nếu có format lop-11-chuong-1-bai-1
        const filenameMatch = filename.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/i)
        if (filenameMatch) {
          const [, lop, chuong, bai] = filenameMatch
          const lopPadded = lop.padStart(2, '0')
          const baiPadded = bai.padStart(2, '0')
          const fixedPath = `/teacher_content/${teacherId}/L${lopPadded}/C${chuong}/B${baiPadded}/${filename}`
          pathsToTry.push(fixedPath)
          console.log(`✅ [Path Fix] Thêm L/C/B từ filename: ${fixedPath}`)
        }
      }

      // Nếu path có /images/ hoặc /videos/, loại bỏ và thử với cấu trúc L{lop}/C{chuong}/B{bai}
      if (cleanPath.includes('/images/') || cleanPath.includes('/videos/')) {
        // Loại bỏ /images/ hoặc /videos/
        const pathWithoutMediaFolder = cleanPath.replace(/\/images\//, '/').replace(/\/videos\//, '/')

        // Thử extract L{lop}/C{chuong}/B{bai} từ filename hoặc path
        const filenameMatch = pathWithoutMediaFolder.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/i)
        if (filenameMatch && teacherId) {
          const [, lop, chuong, bai] = filenameMatch
          const lopPadded = lop.padStart(2, '0')
          const baiPadded = bai.padStart(2, '0')
          const filename = pathWithoutMediaFolder.split('/').pop() || ''
          const newPath = `/teacher_content/${teacherId}/L${lopPadded}/C${chuong}/B${baiPadded}/${filename}`
          pathsToTry.push(newPath)
          console.log(`🔧 [Path Fix] Created path from images/videos folder: ${newPath}`)
        }

        // Cũng thử path không có folder images/videos
        pathsToTry.push(pathWithoutMediaFolder)
      }

      // Thêm path gốc (nếu đã đúng cấu trúc L{lop}/C{chuong}/B{bai})
      pathsToTry.push(cleanPath)

      // Remove duplicates
      const uniquePaths = [...new Set(pathsToTry)]

      // ✅ FIX: Chỉ dùng API proxy cho video files (phần 3 - simulation), images (phần 1) dùng direct URL
      const isVideoFile = /\.(mp4|webm|ogg|mov)/i.test(cleanPath)

      for (const tryPath of uniquePaths) {
        if (isVideoFile) {
          // Video files: dùng API proxy để tránh CORS
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
          const relativePath = tryPath.replace('/teacher_content/', '')
          const proxyUrl = `${API_BASE_URL}/teacher/media?path=${encodeURIComponent(relativePath)}`
          console.log(`🔧 [Python Backend] Using proxy for video: ${tryPath} → ${proxyUrl}`)
          return proxyUrl
        } else {
          // Images (phần 1 - theory): dùng direct URL như cũ (không bị ảnh hưởng)
          const fullUrl = `${baseUrl}${tryPath}`
          console.log(`🌐 [Phần 1 - Theory] Resolving image: ${path} → ${fullUrl}`)

          // Try to verify URL is accessible
          try {
            const testResponse = await fetch(fullUrl, { method: 'HEAD', mode: 'no-cors' })
            // If fetch succeeds (even with no-cors), URL is likely valid
            console.log(`✅ [Phần 1 - Theory] Image URL accessible: ${fullUrl}`)
            return fullUrl
          } catch (error) {
            console.warn(`⚠️ [Phần 1 - Theory] Image URL not accessible: ${fullUrl}, trying next path...`)
          }
        }
      }
    }

    if (typeof window === 'undefined') {
      return path
    }

    // Try to load from IndexedDB as fallback
    try {
      const blob = await this.getFileFromIndexedDB(path)
      if (blob) {
        this.registerPreviewUrl(path, blob)
        const objectUrl = this.previewUrlCache.get(path)
        if (objectUrl) {
          console.log(`✅ Loaded from IndexedDB fallback: ${path}`)
          return objectUrl
        }
      }
    } catch (error) {
      console.error('❌ Error resolving media source from IndexedDB:', error)
    }

    // Final fallback: construct URL anyway (browser will try to load it)
    if (path.startsWith('/teacher_content/') || path.startsWith('teacher_content/')) {
      const cleanPath = path.startsWith('/') ? path : `/${path}`

      const isVideoFile = /\.(mp4|webm|ogg|mov)/i.test(cleanPath)
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      if (isVideoFile) {
        // Video: dùng API proxy (cần /api nếu có)
        const relativePath = cleanPath.replace('/teacher_content/', '')
        const proxyUrl = `${API_BASE_URL}/teacher/media?path=${encodeURIComponent(relativePath)}`
        console.log(`⚠️ [Python Backend] Using proxy fallback: ${proxyUrl}`)
        return proxyUrl
      } else {
        // Images: dùng direct URL (static file at root)
        // ✅ FIX: Remove /api suffix for static file access
        const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '')
        const finalUrl = `${baseUrl}${cleanPath}`
        console.log(`⚠️ [Python Backend] Using direct URL fallback (fixed): ${finalUrl}`)
        return finalUrl
      }
    }

    // Return path as-is if it's a relative path (might be from public folder)
    return path
  }

  /**
   * Replace URLs trong text với local paths
   */
  replaceUrlsWithLocalPaths(text: string, urlMap: Map<string, string>): string {
    let result = text
    urlMap.forEach((localPath, originalUrl) => {
      // Replace all occurrences
      result = result.replace(new RegExp(escapeRegex(originalUrl), 'g'), localPath)
    })
    return result
  }

  /**
   * Get extension từ URL
   */
  private getExtensionFromUrl(url: string): string | null {
    const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i)
    return match ? match[1] : null
  }

  /**
   * Get extension từ MIME type
   */
  private getExtensionFromMimeType(mimeType: string): string {
    const mimeMap: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      'video/mp4': 'mp4',
      'video/webm': 'webm',
      'video/ogg': 'ogg',
      'video/quicktime': 'mov'
    }

    return mimeMap[mimeType] || 'jpg'
  }

  private sanitizeFileName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^\w\d\-_.]/g, '-')
      .replace(/-+/g, '-')
  }

  private registerPreviewUrl(path: string, blob: Blob) {
    if (typeof window === 'undefined') return

    const existing = this.previewUrlCache.get(path)
    if (existing) {
      URL.revokeObjectURL(existing)
    }

    const objectUrl = URL.createObjectURL(blob)
    this.previewUrlCache.set(path, objectUrl)
  }

  private base64ToBlob(base64: string, contentType = ''): Blob {
    const normalized = base64.includes('base64,') ? base64.split('base64,').pop() || '' : base64

    if (typeof window === 'undefined') {
      const nodeBuffer = (globalThis as any)?.Buffer
      if (!nodeBuffer) {
        throw new Error('Buffer is not available in this environment')
      }
      const buffer = nodeBuffer.from(normalized, 'base64')
      return new Blob([buffer], { type: contentType || 'application/octet-stream' })
    }

    const byteCharacters = atob(normalized)
    const byteArrays: BlobPart[] = []
    const sliceSize = 1024

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      byteArrays.push(new Uint8Array(byteNumbers))
    }

    return new Blob(byteArrays, { type: contentType || 'application/octet-stream' })
  }

  /**
   * Save file to public directory
   * Uploads to WordPress server via API, then stores in IndexedDB as backup
   */
  private async saveFileToPublic(blob: Blob, path: string, teacherId?: string, lessonKey?: string, contentType: string = 'theory'): Promise<string> {
    console.log('🚀 [UPLOAD START] saveFileToPublic called:', {
      path,
      teacherId,
      lessonKey,
      contentType,
      blobSize: blob.size,
      blobType: blob.type
    })

    try {
      // Extract filename from path
      const pathParts = path.split('/').filter(p => p)
      const filename = pathParts.pop() || `file-${Date.now()}.${blob.type.split('/')[1] || 'bin'}`

      // Parse path để lấy subDirectory (L{lop}/C{chuong}/B{bai})
      // Path format: /teacher_content/{teacherId}/L{lop}/C{chuong}/B{bai}/{filename}
      let relativeSubdir = ''
      if (path.includes('/L') && path.includes('/C') && path.includes('/B')) {
        // Extract L{lop}/C{chuong}/B{bai} từ path
        const match = path.match(/\/L(\d+)\/C(\d+)\/B(\d+)\//)
        if (match) {
          relativeSubdir = `L${match[1]}/C${match[2]}/B${match[3]}`
          console.log(`📁 Extracted subDirectory from path: ${relativeSubdir}`)
        }
      } else if (lessonKey) {
        // Nếu path chưa có cấu trúc L{lop}/C{chuong}/B{bai}, parse từ lessonKey
        const lessonInfo = this.parseLessonKey(lessonKey)
        if (lessonInfo) {
          relativeSubdir = `L${lessonInfo.lop}/C${lessonInfo.chuong}/B${lessonInfo.bai}`
          console.log(`📁 Extracted subDirectory from lessonKey: ${relativeSubdir}`)
        }
      }

      // Convert blob to base64 for API
      const base64Data = await this.blobToBase64(blob)
      const dataUrl = `data:${blob.type};base64,${base64Data}`

      // Try to upload to WordPress server via API
      let serverPath = path // Fallback to original path

      try {
        const token = this.getAuthToken()
        if (token && teacherId) {
          // ✅ UPDATED: Use Python backend instead of WordPress
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 seconds timeout

          const response = await fetch(`${API_BASE_URL}/teacher/assets`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              teacherId,
              lessonKey: lessonKey || '',
              contentType,
              fileName: path.includes('/images/') ? `images/${filename}` : filename, // ✅ Add images/ prefix if path contains it
              mimeType: blob.type,
              fileData: dataUrl,
              subDirectory: relativeSubdir // Pass the subdirectory to the backend (L{lop}/C{chuong}/B{bai})
            }),
            signal: controller.signal
          })

          clearTimeout(timeoutId)

          if (response.ok) {
            const result = await response.json()
            if (result.success && result.path) {
              // ✅ FIX: Ưu tiên sử dụng path từ server (có filename đầy đủ với timestamp)
              // Backend trả về result.path với tên file đầy đủ (có timestamp)
              serverPath = result.path // Use server path (includes full filename with timestamp)
              console.log(`✅ File uploaded to server: ${serverPath}`)
              console.log(`🌐 Public URL: ${result.url}`)
              console.log(`📝 Server filename: ${result.fileName || 'N/A'}`)

              // ✅ FIX: Đảm bảo path sử dụng đúng fileName đầy đủ từ server
              // Nếu result.fileName có và khác filename ban đầu, update path
              if (result.fileName && result.fileName !== filename) {
                // Extract directory từ result.path (đã có tên đầy đủ)
                // Hoặc build lại path với fileName đầy đủ
                const pathDir = serverPath.substring(0, serverPath.lastIndexOf('/'))
                const updatedPath = `${pathDir}/${result.fileName}`
                // Chỉ update nếu path khác (đảm bảo dùng tên đầy đủ)
                if (updatedPath !== serverPath) {
                  serverPath = updatedPath
                  console.log(`🔧 [Phần 3 - Simulation] Updated path with full server filename: ${serverPath}`)
                }
              }

              // ✅ FIX: Log để debug
              console.log(`📦 [Phần 3 - Simulation] Final serverPath: ${serverPath}`)
            } else {
              console.warn('⚠️ Upload API returned success but no path, using fallback')
            }
          } else {
            const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }))
            console.warn(`⚠️ Failed to upload to server (${response.status}): ${error.message}, using IndexedDB fallback`)
          }
        } else {
          console.warn('⚠️ No auth token or teacherId, skipping server upload, using IndexedDB')
        }
      } catch (uploadError: any) {
        if (uploadError.name === 'AbortError') {
          console.warn('⚠️ Upload timeout, using IndexedDB fallback')
        } else {
          console.warn('⚠️ Error uploading to server, using IndexedDB fallback:', uploadError.message)
        }
      }

      // Store in IndexedDB as backup (always do this)
      await this.storeInIndexedDB(blob, serverPath)
      console.log(`💾 Stored file in IndexedDB: ${serverPath}`)

      return serverPath
    } catch (error) {
      console.error(`❌ Error saving file to ${path}:`, error)
      throw error
    }
  }

  /**
   * Convert blob to base64 string
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        // Remove data URL prefix if present
        const base64 = base64String.includes(',') ? base64String.split(',')[1] : base64String
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('wp_token') ||
      null
  }

  /**
   * Store blob in IndexedDB
   */
  private async storeInIndexedDB(blob: Blob, path: string): Promise<void> {
    const buffer = await blob.arrayBuffer()

    return new Promise((resolve, reject) => {
      const request = indexedDB.open('TeacherContentDB', 6) // Sync with TeacherContentService version

      request.onerror = () => reject(request.error)
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result

        // Create databases store if not exists (sync with TeacherContentService)
        if (!db.objectStoreNames.contains('databases')) {
          db.createObjectStore('databases', { keyPath: 'id' })
        }

        // Create media store if not exists
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'path' })
        }

        // Create quizzes store if not exists
        if (!db.objectStoreNames.contains('quizzes')) {
          db.createObjectStore('quizzes', { keyPath: 'id' })
        }
      }
      request.onsuccess = () => {
        const db = request.result

        // Ensure object stores exist before using them
        if (!db.objectStoreNames.contains('media')) {
          reject(new Error('Media object store not found in IndexedDB'))
          return
        }

        const transaction = db.transaction(['media'], 'readwrite')
        const store = transaction.objectStore('media')

        const putRequest = store.put({ path, data: buffer, timestamp: Date.now() })
        putRequest.onerror = () => reject(putRequest.error)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(transaction.error)
      }
    })
  }


  /**
   * Get file from IndexedDB
   */
  async getFileFromIndexedDB(path: string): Promise<Blob | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('TeacherContentDB', 6) // Sync with TeacherContentService version

      request.onerror = () => reject(request.error)
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result

        // Create databases store if not exists (sync with TeacherContentService)
        if (!db.objectStoreNames.contains('databases')) {
          db.createObjectStore('databases', { keyPath: 'id' })
        }

        // Create media store if not exists
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'path' })
        }

        // Create quizzes store if not exists
        if (!db.objectStoreNames.contains('quizzes')) {
          db.createObjectStore('quizzes', { keyPath: 'id' })
        }
      }
      request.onsuccess = () => {
        const db = request.result

        // Ensure object stores exist before using them
        if (!db.objectStoreNames.contains('media')) {
          console.warn('⚠️ Media object store not found in IndexedDB, returning null')
          resolve(null)
          return
        }

        const transaction = db.transaction(['media'], 'readonly')
        const store = transaction.objectStore('media')
        const getRequest = store.get(path)

        getRequest.onsuccess = () => {
          if (getRequest.result) {
            resolve(new Blob([getRequest.result.data]))
          } else {
            resolve(null)
          }
        }
        getRequest.onerror = () => reject(getRequest.error)
      }
    })
  }
}

// Helper function
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const imageExtractor = new ImageExtractor()
export default imageExtractor

